<template>
  <v-container fluid dark outlined>
    <v-card dark outlined>
      <v-card-title class="text-h6">
        {{ formTitle }}
      </v-card-title>
      <v-divider></v-divider>

      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card>
          <!-- Header Information -->
          <v-row>
            <v-col>
              <label style="padding-left: 12px">
                <B>Event Name: </B>
                {{ selectedEvent.EventName || "N/A" }}
              </label>
              <br /><br />
              <label style="padding-left: 12px">
                <B>Stallholder Name: </B>
                {{
                  selectedStallholder.StallholderName ||
                  "No Stallholder selected"
                }}
              </label>
            </v-col>
          </v-row>

          <!-- Stall Selection and Layout Name -->
          <v-row>
            <v-col cols="3" class="mt-3">
              <v-combobox
                v-model="selectedStalls"
                multiple
                :items="availableStalls"
                return-object
                label="Stalls"
                item-text="StallName"
                style="padding-left: 12px"
                @change="updateSelectedStalls"
              >
              </v-combobox>
            </v-col>
            <v-col cols="1"></v-col>
            <v-col cols="4" class="mt-3">
              <v-text-field
                v-model="layoutName"
                label="Layout Name"
                required
                style="padding-right: 6px"
                :rules="[(v) => !!v || 'Layout name is required']"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Breadcrumb Navigation -->
          <v-row v-if="layoutName">
            <v-col cols="12">
              <LayoutBreadcrumb @navigate="onBreadcrumbNavigate" />
            </v-col>
          </v-row>

          <!-- Layout Editor -->
          <v-container fluid v-if="layoutName">
            <LayoutEditor @validation-error="showValidationError" />
          </v-container>
        </v-card>
      </v-form>

      <!-- Action Buttons -->
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="cancel">Cancel</v-btn>
        <v-btn
          color="blue darken-1"
          text
          @click="save"
          :disabled="!canSave"
          :loading="saving"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { mapState, mapActions, mapMutations } from "vuex";
import LayoutBreadcrumb from "./LayoutBreadcrumb.vue";
import LayoutEditor from "./LayoutEditor.vue";

export default {
  name: "LayoutManager",

  components: {
    LayoutBreadcrumb,
    LayoutEditor,
  },

  props: {
    mode: {
      type: String,
      default: "create", // 'create' or 'update'
      validator: (value) => ["create", "update"].includes(value),
    },
    layoutData: {
      type: Object,
      default: null,
    },
    stallholderData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      valid: true,
      loading: false,
      saving: false,
      selectedStalls: [],
      availableStalls: [],
    };
  },

  computed: {
    ...mapState("filter", ["globalEventFilter"]),
    ...mapState("admin", [
      "currentLayout",
      "editingPath",
      "layoutAction",
      "currentStallholder",
    ]),

    selectedEvent() {
      return this.globalEventFilter;
    },

    selectedStallholder() {
      return this.stallholderData || this.currentStallholder || {};
    },

    layoutName: {
      get() {
        return this.currentLayout.LayoutName;
      },
      set(value) {
        this.updateLayoutName(value);
      },
    },

    formTitle() {
      return this.layoutAction === "create" ? "New Layout" : "Edit Layout";
    },

    canSave() {
      // WE can save without stallid && this.selectedStalls.length > 0
      return this.layoutName && this.valid;
    },
  },

  async created() {
    this.setEventSelectorDisabled(true);
    await this.initializeComponent();
  },

  beforeDestroy() {
    this.setEventSelectorDisabled(false);
  },

  methods: {
    ...mapActions("admin", ["initializeLayout", "saveLayout"]),
    ...mapMutations("admin", [
      "setCurrentLayout",
      "navigateToPath",
      "setEventSelectorDisabled",
      "setCurrentStallholder",
      "clearCurrentLayout",
    ]),

    async initializeComponent() {
      try {
        this.loading = true;

        // Use props data instead of localStorage
        const stallholder = this.stallholderData || this.currentStallholder;

        if (!this.selectedEvent.EventId || !stallholder?.StallholderId) {
          throw new Error("Missing required event or stallholder data");
        }

        // Set current stallholder in store if passed via props
        if (this.stallholderData) {
          this.setCurrentStallholder(this.stallholderData);
        }

        // Set available stalls from stallholder
        this.availableStalls = stallholder.Stalls || [];

        // Initialize the layout
        await this.initializeLayout({
          layoutData: this.layoutData,
          eventId: this.selectedEvent.EventId,
          stallholderId: stallholder.StallholderId,
          action: this.mode,
        });

        // Set selected stalls if editing existing layout
        if (this.layoutData && this.layoutData.Stalls) {
          this.selectedStalls = this.layoutData.Stalls;
        }
      } catch (error) {
        console.error("Error initializing layout manager:", error);
        this.setEventSelectorDisabled(false);
        iziToast.error({
          title: "Error",
          message: "Failed to initialize layout manager",
          position: "topRight",
        });
        this.goBack();
      } finally {
        this.loading = false;
      }
    },

    updateLayoutName(name) {
      this.setCurrentLayout({
        ...this.currentLayout,
        LayoutName: name,
      });
    },

    updateSelectedStalls() {
      const processedStalls = this.selectedStalls.map((stall) => ({
        StallId: stall.StallId,
        StallName: stall.StallName,
      }));

      this.setCurrentLayout({
        ...this.currentLayout,
        Stalls: processedStalls,
      });
    },

    onBreadcrumbNavigate() {
      // Navigation is handled by the breadcrumb component mutations
    },

    showValidationError(message) {
      iziToast.warning({
        title: "Validation Error",
        message: message,
        position: "topRight",
      });
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.saving = true;

        if (
          !this.selectedEvent.EventId ||
          !this.currentStallholder?.StallholderId
        ) {
          throw new Error("Missing required event or stallholder data");
        }

        await this.saveLayout({
          eventId: this.selectedEvent.EventId,
          stallholderId: this.currentStallholder.StallholderId,
          stalls: this.selectedStalls,
        });

        iziToast.success({
          title: "Layout",
          message: "Successfully saved layout!",
          position: "topRight",
        });

        this.setEventSelectorDisabled(false);
        this.clearCurrentLayout();
        this.goBack();
      } catch (error) {
        console.error("Error saving layout:", error);
        this.setEventSelectorDisabled(false);
        // Don't clear the layout on error - preserve user's work
        iziToast.error({
          title: "Error",
          message: "Failed to save layout. Please try again.",
          position: "topRight",
        });
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.setEventSelectorDisabled(false);
      this.clearCurrentLayout();
      this.goBack();
    },

    goBack() {
      this.setEventSelectorDisabled(false);
      this.$router.push({
        name: "ProductManagement",
      });
    },
  },
};
</script>

<style scoped>
h1 {
  color: #a09c94;
  font-family: sans-serif;
}

.v-overlay {
  z-index: 10;
}
</style>
